import type { ThemeColorName } from '@examdojo/styles';
import type { Value, PointTarget, EdgeTarget, GeometryElementDisplayConfig } from '../../../geometrypad.model';

// =============================================================================
// TYPES
// =============================================================================

/** Rectangle construction format (used for explicit format override; auto-detected when omitted) */
export type RectangleFormat = 'WH' | 'WD' | 'HD';

export type RectangleProperty = 'w' | 'h' | 'd';
export type RectangleVertexName = 'A' | 'B' | 'C' | 'D';
export type RectangleEdgeName = 'bottom' | 'right' | 'top' | 'left';

export interface RectangleElement {
  type: 'Rectangle';
  params: {
    w?: Value;
    h?: Value;
    d?: Value;
    A?: PointTarget;
    B?: PointTarget;
    C?: PointTarget;
    D?: PointTarget;
    bottom?: EdgeTarget;
    right?: EdgeTarget;
    top?: EdgeTarget;
    left?: EdgeTarget;
  };
  names?: { w?: string; h?: string; d?: string };
}

// =============================================================================
// STRUCTURAL CONSTANTS
// =============================================================================

export const EDGE_NAMES: readonly string[] = ['bottom', 'right', 'top', 'left'] as const;
export const PROPERTY_FOR_EDGE: Record<string, 'w' | 'h'> = {
  bottom: 'w',
  right: 'h',
  top: 'w',
  left: 'h',
};

// =============================================================================
// DISPLAY DEFAULTS
// =============================================================================

/**
 * Default display theme for rectangles.
 *
 * Edge colors: bottom=sky, right=pink, top=purple, left=teal
 * Angle colors: alpha=sky, beta=pink, gamma=purple, delta=teal
 */
export const RECTANGLE_DEFAULTS: GeometryElementDisplayConfig = {
  polygon: { fillColor: 'sky', fillOpacity: 0.1 },
  line: { strokeWidth: 2, color: 'neutral' },
  point: { visible: false, size: 3 },
  angle: { fillOpacity: 0.2, radius: 0.4, visible: false },
  subparts: {
    bottom: { line: { color: 'sky' } },
    right: { line: { color: 'pink' } },
    top: { line: { color: 'purple' } },
    left: { line: { color: 'teal' } },
    // Angles (90 degree markers, hidden by default)
    alpha: { angle: { fillColor: 'sky', strokeColor: 'sky', visible: false } },
    beta: { angle: { fillColor: 'pink', strokeColor: 'pink', visible: false } },
    gamma: { angle: { fillColor: 'purple', strokeColor: 'purple', visible: false } },
    delta: { angle: { fillColor: 'teal', strokeColor: 'teal', visible: false } },
    // Points
    A: { point: { color: 'sky' } },
    B: { point: { color: 'pink' } },
    C: { point: { color: 'purple' } },
    D: { point: { color: 'teal' } },
  },
};

/** @deprecated Use RECTANGLE_DEFAULTS.subparts instead */
export const RECTANGLE_EDGE_COLORS: ThemeColorName[] = ['sky', 'pink', 'purple', 'teal'];
